import { supabaseClient } from './supabase';
import { supabaseLocationService } from './supabaseLocationService';
import { CALIFORNIA_STATE_GEOFIPS } from './californiaGhgInventoryVmtService';

export interface ConsumptionBasedGHGData {
  year: number;
  transportation: number;
  housing: number;
  food: number;
  goods: number;
  services: number;
}

export interface ConsumptionBasedGHGResult {
  chartData: {
    labels: number[];
    datasets: Array<{
      label: string;
      data: number[];
      backgroundColor: string;
      borderColor: string;
      fill?: boolean;
      tension?: number;
    }>;
  };
  chartOptions: any;
  totalEmissions: number;
  latestYear: number;
  percentChange: number;
  transportationPercent: number;
  housingPercent: number;
  foodPercent: number;
  goodsPercent: number;
  servicesPercent: number;
  // New smart metrics
  californiaAverage: number;
  locationVsCalifornia: number; // percentage
  transportationShare: number; // percentage
}

const PAGE_SIZE = 1000;

/** California county band in `cbei_counties` (same as GHG county inventory). */
const CA_CBEI_GEOFIPS_MIN = 6000;
const CA_CBEI_GEOFIPS_MAX_EXCLUSIVE = 7000;

type CbeiCountyRow = {
  geofips: number | string;
  year: number;
  transportation_total_emissions: number | null;
  housing_total_emissions: number | null;
  food_total_emissions: number | null;
  goods_total_emissions: number | null;
  services_total_emissions: number | null;
  hhcount: number | null;
};

function contributionMt(row: CbeiCountyRow, field: keyof Pick<CbeiCountyRow, 'transportation_total_emissions' | 'housing_total_emissions' | 'food_total_emissions' | 'goods_total_emissions' | 'services_total_emissions'>): number {
  const hh = Number(row.hhcount) > 0 ? Number(row.hhcount) : 1;
  const v = Number(row[field]) || 0;
  return (v / 1_000_000) * hh;
}

/**
 * Sum `cbei_counties` for all California counties (geofips 6000–6999), deduped by (geofips, year),
 * then aggregate totals by year. Used for California State geography and for statewide comparison baselines.
 */
export async function fetchCaliforniaConsumptionBasedSummedFromCounties(): Promise<ConsumptionBasedGHGData[]> {
  const raw: CbeiCountyRow[] = [];
  let from = 0;

  for (;;) {
    const { data, error } = await supabaseClient
      .from('cbei_counties')
      .select(
        'geofips,year,transportation_total_emissions,housing_total_emissions,food_total_emissions,goods_total_emissions,services_total_emissions,hhcount'
      )
      .gte('geofips', CA_CBEI_GEOFIPS_MIN)
      .lt('geofips', CA_CBEI_GEOFIPS_MAX_EXCLUSIVE)
      .order('geofips', { ascending: true })
      .order('year', { ascending: true })
      .range(from, from + PAGE_SIZE - 1);

    if (error) {
      console.error('fetchCaliforniaConsumptionBasedSummedFromCounties:', error);
      throw error;
    }

    const batch = (data ?? []) as CbeiCountyRow[];
    if (batch.length === 0) break;
    raw.push(...batch);
    if (batch.length < PAGE_SIZE) break;
    from += PAGE_SIZE;
  }

  const byGeofipsYear = new Map<string, CbeiCountyRow>();
  for (const r of raw) {
    const g = Number(r.geofips);
    const y = Number(r.year);
    if (!Number.isFinite(g) || !Number.isFinite(y)) continue;
    byGeofipsYear.set(`${g}|${y}`, r);
  }

  type Acc = { t: number; h: number; f: number; g: number; s: number };
  const yearAcc = new Map<number, Acc>();

  for (const row of Array.from(byGeofipsYear.values())) {
    const y = Number(row.year);
    if (!yearAcc.has(y)) {
      yearAcc.set(y, { t: 0, h: 0, f: 0, g: 0, s: 0 });
    }
    const a = yearAcc.get(y)!;
    a.t += contributionMt(row, 'transportation_total_emissions');
    a.h += contributionMt(row, 'housing_total_emissions');
    a.f += contributionMt(row, 'food_total_emissions');
    a.g += contributionMt(row, 'goods_total_emissions');
    a.s += contributionMt(row, 'services_total_emissions');
  }

  const years = Array.from(yearAcc.keys()).sort((a, b) => a - b);
  return years.map((year) => {
    const a = yearAcc.get(year)!;
    return {
      year,
      transportation: Math.round(a.t),
      housing: Math.round(a.h),
      food: Math.round(a.f),
      goods: Math.round(a.g),
      services: Math.round(a.s)
    };
  });
}

function processCbeiRowsToHistoricalData(data: CbeiCountyRow[]): ConsumptionBasedGHGData[] {
  return data.map((row) => {
    const hhcount = Number(row.hhcount) > 0 ? Number(row.hhcount) : 1;
    return {
      year: row.year,
      transportation: Math.round(((Number(row.transportation_total_emissions) || 0) / 1_000_000) * hhcount),
      housing: Math.round(((Number(row.housing_total_emissions) || 0) / 1_000_000) * hhcount),
      food: Math.round(((Number(row.food_total_emissions) || 0) / 1_000_000) * hhcount),
      goods: Math.round(((Number(row.goods_total_emissions) || 0) / 1_000_000) * hhcount),
      services: Math.round(((Number(row.services_total_emissions) || 0) / 1_000_000) * hhcount)
    };
  });
}

function buildConsumptionBasedResult(
  location: string,
  historicalData: ConsumptionBasedGHGData[],
  options?: { isCaliforniaStateView?: boolean }
): ConsumptionBasedGHGResult {
  const isCaliforniaStateView = options?.isCaliforniaStateView ?? false;

  const years = historicalData.map((d) => d.year);
  const categories = ['Transportation', 'Housing', 'Food', 'Goods', 'Services'];
  const colors = ['#1e3a8a', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

  const datasets = categories.map((category, index) => {
    const fieldMap: { [key: string]: keyof ConsumptionBasedGHGData } = {
      Transportation: 'transportation',
      Housing: 'housing',
      Food: 'food',
      Goods: 'goods',
      Services: 'services'
    };

    const field = fieldMap[category];
    const data = historicalData.map((d) => d[field]);

    return {
      label: category,
      data,
      backgroundColor: colors[index],
      borderColor: colors[index],
      fill: false,
      tension: 0.2
    };
  });

  const latestData = historicalData[historicalData.length - 1];
  const earliestData = historicalData[0];
  const totalEmissions =
    latestData.transportation +
    latestData.housing +
    latestData.food +
    latestData.goods +
    latestData.services;
  const earliestTotal = earliestData
    ? earliestData.transportation +
      earliestData.housing +
      earliestData.food +
      earliestData.goods +
      earliestData.services
    : 0;
  const percentChange =
    earliestData && earliestTotal > 0
      ? ((totalEmissions - earliestTotal) / earliestTotal) * 100
      : 0;

  const transportationPercent = totalEmissions > 0 ? (latestData.transportation / totalEmissions) * 100 : 0;
  const housingPercent = totalEmissions > 0 ? (latestData.housing / totalEmissions) * 100 : 0;
  const foodPercent = totalEmissions > 0 ? (latestData.food / totalEmissions) * 100 : 0;
  const goodsPercent = totalEmissions > 0 ? (latestData.goods / totalEmissions) * 100 : 0;
  const servicesPercent = totalEmissions > 0 ? (latestData.services / totalEmissions) * 100 : 0;

  let californiaAverage = 0;
  let locationVsCalifornia = 0;

  if (isCaliforniaStateView) {
    californiaAverage = totalEmissions;
    locationVsCalifornia = 100;
  }

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          usePointStyle: true,
          padding: 20
        }
      },
      title: {
        display: true,
        text: isCaliforniaStateView
          ? `Consumption-Based GHG Inventory — ${location} (sum of CA counties in Supabase)`
          : `Consumption-Based GHG Inventory - ${location}`,
        font: { size: 16, weight: 'bold' as const }
      },
      tooltip: {
        callbacks: {
          label: function (context: any) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            label += new Intl.NumberFormat('en-US', { style: 'decimal' }).format(context.parsed.y) + ' MT CO₂e';
            return label;
          }
        }
      }
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Year'
        }
      },
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Emissions (Metric Tons CO₂e per Community)'
        },
        ticks: {
          callback: (value: any) => Number(value).toLocaleString()
        }
      }
    }
  };

  return {
    chartData: {
      labels: years,
      datasets
    },
    chartOptions,
    totalEmissions,
    latestYear: latestData.year,
    percentChange,
    transportationPercent,
    housingPercent,
    foodPercent,
    goodsPercent,
    servicesPercent,
    californiaAverage,
    locationVsCalifornia,
    transportationShare: transportationPercent
  };
}

/** Resolve California statewide consumption total for `year` (sum of counties). */
export async function getCaliforniaConsumptionTotalForYear(year: number): Promise<number> {
  const series = await fetchCaliforniaConsumptionBasedSummedFromCounties();
  const row = series.find((r) => r.year === year);
  if (!row) return 0;
  return row.transportation + row.housing + row.food + row.goods + row.services;
}

export async function fetchConsumptionBasedGHGData(location: string): Promise<ConsumptionBasedGHGResult> {
  try {
    console.log('🔍 Fetching consumption-based GHG data for:', location);

    await supabaseLocationService.initialize();

    const geofips = await supabaseLocationService.getGeofips(location);
    if (!geofips) {
      throw new Error(`Could not find geofips for location: ${location}`);
    }

    console.log('🔍 Geofips for location:', geofips);

    const { data: locationData, error: locationError } = await supabaseClient
      .from('population')
      .select('location_name, geography_type, county')
      .eq('geofips', geofips)
      .limit(1);

    if (locationError) {
      console.error('Error fetching location data:', locationError);
      throw locationError;
    }

    if (!locationData || locationData.length === 0) {
      throw new Error(`No location data found for geofips: ${geofips}`);
    }

    const locationInfo = locationData[0];
    console.log('🔍 Location info:', locationInfo);

    const geoType = String(locationInfo.geography_type || '');
    const isCaliforniaState = geoType === 'State' && Number(geofips) === CALIFORNIA_STATE_GEOFIPS;

    let historicalData: ConsumptionBasedGHGData[];
    let tableName: string;

    if (isCaliforniaState) {
      tableName = 'cbei_counties (summed CA counties)';
      historicalData = await fetchCaliforniaConsumptionBasedSummedFromCounties();
      console.log('🔍 California State: consumption-based = sum of cbei_counties rows', historicalData.length, 'years');
      if (!historicalData.length) {
        throw new Error(
          `No consumption-based GHG data for California (no cbei_counties rows with geofips ${CA_CBEI_GEOFIPS_MIN}–${CA_CBEI_GEOFIPS_MAX_EXCLUSIVE - 1}).`
        );
      }
      const base = buildConsumptionBasedResult(location, historicalData, { isCaliforniaStateView: true });
      return await attachCaliforniaComparisonMetrics(location, base, historicalData, true);
    }

    let queryGeofips = geofips;

    if (geoType === 'City') {
      tableName = 'cbei_cities';
      console.log('🔍 Using cbei_cities table for city');
    } else if (geoType === 'County') {
      tableName = 'cbei_counties';
      console.log('🔍 Using cbei_counties table for county');
    } else {
      throw new Error(`Unsupported geography type: ${locationInfo.geography_type}`);
    }

    const { data, error } = await supabaseClient
      .from(tableName === 'cbei_cities' ? 'cbei_cities' : 'cbei_counties')
      .select(
        'year, transportation_total_emissions, housing_total_emissions, food_total_emissions, goods_total_emissions, services_total_emissions, hhcount'
      )
      .eq('geofips', queryGeofips)
      .order('year', { ascending: true });

    if (error) {
      console.error(`Error fetching consumption-based GHG data from ${tableName}:`, error);
      throw error;
    }

    console.log('🔍 Raw data from', tableName, ':', data?.length || 0, 'records');

    if (!data || data.length === 0) {
      throw new Error(`No consumption-based GHG data found for ${location} in ${tableName}`);
    }

    historicalData = processCbeiRowsToHistoricalData(data as CbeiCountyRow[]);

    console.log('🔍 Historical data processed:', historicalData.length, 'years of real data');

    const base = buildConsumptionBasedResult(location, historicalData, { isCaliforniaStateView: false });
    return await attachCaliforniaComparisonMetrics(location, base, historicalData, false);
  } catch (error) {
    console.error('Error fetching consumption-based GHG data:', error);
    throw error;
  }
}

/**
 * Fill californiaAverage / locationVsCalifornia using summed county statewide series (not geofips=6000).
 */
async function attachCaliforniaComparisonMetrics(
  _location: string,
  base: ConsumptionBasedGHGResult,
  historicalData: ConsumptionBasedGHGData[],
  isCaliforniaStateView: boolean
): Promise<ConsumptionBasedGHGResult> {
  const latestData = historicalData[historicalData.length - 1];
  const totalEmissions = base.totalEmissions;

  let californiaAverage = base.californiaAverage;
  let locationVsCalifornia = base.locationVsCalifornia;

  if (isCaliforniaStateView) {
    return { ...base, californiaAverage, locationVsCalifornia };
  }

  try {
    const caSeries = await fetchCaliforniaConsumptionBasedSummedFromCounties();
    const caRow = caSeries.find((r) => r.year === latestData.year);
    if (caRow) {
      californiaAverage =
        caRow.transportation + caRow.housing + caRow.food + caRow.goods + caRow.services;
      locationVsCalifornia = californiaAverage > 0 ? (totalEmissions / californiaAverage) * 100 : 0;
      console.log('🔍 California comparison (county sum):', {
        californiaAverage,
        totalEmissions,
        locationVsCalifornia,
        year: latestData.year
      });
    } else {
      californiaAverage = 15.2 * 100000;
      locationVsCalifornia = californiaAverage > 0 ? (totalEmissions / californiaAverage) * 100 : 0;
      console.log('🔍 No CA county-sum row for year', latestData.year, '- using fallback average');
    }
  } catch (e) {
    console.warn('Error fetching California county-sum consumption for comparison:', e);
    californiaAverage = 15.2 * 100000;
    locationVsCalifornia = californiaAverage > 0 ? (totalEmissions / californiaAverage) * 100 : 0;
  }

  return {
    ...base,
    californiaAverage,
    locationVsCalifornia
  };
}
